import SearchInput from '../../../components/ui/SearchInput';
import { Dialog } from '../../../components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { getAllAssessmentToClassroomInUser } from '../../../services/user/classroom.services';
import { encodeData } from '../../../utils/encodeAndEncode';
import Loader from '../../../components/common/Loader';
import useClassroomRealtime from '../../../hooks/useClassroomRealtime';
import { CLASSROOM_REALTIME_EVENTS } from '../../../services/classroomRealtime.service';
import { scheduleAssessmentTimers } from '../../../utils/classroomScheduleTimers';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';
import { ensureMinLoader } from '../../../utils/loaderDelay';

function Assessments({ classroom_id }) {
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const navigate = useNavigate();
  const { timeZone } = useTimezone();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [assessments, setAssessments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const userTimeZone = timeZone;

  const getAssessmentId = (assessment) =>
    Number(assessment?.assessment_id ?? assessment?.id);

  const patchAssessment = useCallback((assessmentId, patch) => {
    setAssessments((prev) =>
      prev.map((assessment) =>
        getAssessmentId(assessment) === Number(assessmentId)
          ? { ...assessment, ...patch }
          : assessment
      )
    );
    setSelectedAssessment((prev) =>
      prev && getAssessmentId(prev) === Number(assessmentId) ? { ...prev, ...patch } : prev
    );
  }, []);

  const parseUtcDate = (value) => {
    if (!value) return null;
    const parsed = moment.utc(
      value,
      [
        moment.ISO_8601,
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DDTHH:mm:ss",
        "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
        "YYYY-MM-DDTHH:mm:ss[Z]",
      ],
      true
    );
    return parsed.isValid() ? parsed : null;
  };

  const formatDate = (utcDateString) => {
    return formatUtcInTimeZone(utcDateString, userTimeZone, 'MMM DD, YYYY hh:mm A');
  };

  useEffect(() => {
    const fetchAssessments = async () => {
      const startedAt = Date.now();
      try {
        setIsLoading(true);
        const payload = {
          class_id: classroom_id,
          user_id: userId,
          page: page,
          limit: itemsPerPage,
          search: search
        };

        const response = await getAllAssessmentToClassroomInUser(payload);
        const body = response?.data ?? response;
        const success = body?.success ?? response?.success;
        const list = body?.data?.assessments ?? body?.assessments;
        if (success && Array.isArray(list)) {
          setAssessments(list);
        } else {
          setAssessments([]);
        }
      } catch (error) {
        console.error("Error fetching assessments:", error);
        setAssessments([]);
      } finally {
        if (page === 1) await ensureMinLoader(startedAt);
        setIsLoading(false);
      }
    };

    if (classroom_id && userId) {
      fetchAssessments();
    }
  }, [classroom_id, userId, page, itemsPerPage, search, reloadToken]);

  useClassroomRealtime(classroom_id, {
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_ACTIVATED]: (payload) => {
      patchAssessment(payload.assessmentId, {
        is_active: 1,
        activate_time: payload.activate_time,
        deactivate_time: payload.deactivate_time,
      });
    },
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_DEACTIVATED]: (payload) => {
      patchAssessment(payload.assessmentId, {
        is_active: 0,
        activate_time: payload.activate_time,
        deactivate_time: payload.deactivate_time,
      });
    },
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_VISIBILITY_CHANGED]: (payload) => {
      if (Number(payload.is_visible) === 0) {
        setAssessments((prev) =>
          prev.filter((assessment) => getAssessmentId(assessment) !== Number(payload.assessmentId))
        );
        return;
      }
      setReloadToken((value) => value + 1);
    },
    [CLASSROOM_REALTIME_EVENTS.ASSESSMENT_UPDATED]: (payload) => {
      const assessmentId = Number(payload.assessmentId ?? payload.assessment_id);
      if (Number(payload.is_visible) === 0) {
        setAssessments((prev) =>
          prev.filter((assessment) => getAssessmentId(assessment) !== assessmentId)
        );
        return;
      }

      setAssessments((prev) => {
        const exists = prev.some((assessment) => getAssessmentId(assessment) === assessmentId);
        if (!exists) {
          setReloadToken((value) => value + 1);
          return prev;
        }
        return prev.map((assessment) =>
          getAssessmentId(assessment) === assessmentId ? { ...assessment, ...payload } : assessment
        );
      });
    },
  });

  useEffect(() => {
    const clearTimers = scheduleAssessmentTimers(assessments, {
      onActivate: (payload) => {
        patchAssessment(payload.assessmentId, {
          is_active: 1,
          activate_time: payload.activate_time,
          deactivate_time: payload.deactivate_time,
        });
      },
      onDeactivate: (payload) => {
        patchAssessment(payload.assessmentId, {
          is_active: 0,
          activate_time: payload.activate_time,
          deactivate_time: payload.deactivate_time,
        });
      },
    });

    return clearTimers;
  }, [assessments, patchAssessment]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const isAssessmentEnabled = (assessment) => {
    if (!assessment) return false;
    if (assessment.is_time_based === 0) {
      return assessment.is_active === 1;
    }
    const activateTime = parseUtcDate(assessment.activate_time);
    const deactivateTime = parseUtcDate(assessment.deactivate_time);
    if (!activateTime || !deactivateTime) return assessment.is_active === 1;
    const currentTimeUtc = moment.utc();
    return assessment.is_active === 1 && currentTimeUtc.isBetween(activateTime, deactivateTime, null, '[]');
  };

  /** Availability banner: color + label by schedule state (not only “can start now”). */
  const getAvailabilityDisplay = (assessment) => {
    if (!assessment) {
      return { boxClass: 'tw-bg-slate-50   tw-border', titleClass: 'tw-text-slate-500', label: 'Unavailable', showWindow: false };
    }

    const now = moment.utc();

    if (assessment.is_time_based === 0) {
      if (assessment.is_active === 1) {
        return {
          boxClass: 'tw-bg-emerald-50   tw-border   border-success-subtle',
          titleClass: 'tw-text-emerald-600',
          label: 'Available anytime',
          showWindow: false,
        };
      }
      return {
        boxClass: 'tw-bg-slate-50   tw-border',
        titleClass: 'tw-text-slate-500',
        label: 'Always available',
        hint: 'Waiting for instructor to activate',
        showWindow: false,
      };
    }

    const activateTime = parseUtcDate(assessment.activate_time);
    const deactivateTime = parseUtcDate(assessment.deactivate_time);
    const showWindow = Boolean(activateTime && deactivateTime);

    if (!showWindow) {
      const canStart = assessment.is_active === 1;
      return {
        boxClass: canStart ? 'tw-bg-emerald-50   tw-border   border-success-subtle' : 'tw-bg-slate-50   tw-border',
        titleClass: canStart ? 'tw-text-emerald-600' : 'tw-text-slate-500',
        label: canStart ? 'Available now' : 'Schedule unavailable',
        showWindow: false,
      };
    }

    if (now.isBefore(activateTime)) {
      return {
        boxClass: 'tw-bg-brand-50   tw-border   border-primary-subtle',
        titleClass: 'tw-text-brand-600',
        label: 'Opens soon',
        showWindow: true,
      };
    }

    if (now.isAfter(deactivateTime)) {
      return {
        boxClass: 'tw-bg-slate-50   tw-border',
        titleClass: 'tw-text-slate-500',
        label: 'Assessment closed',
        showWindow: true,
      };
    }

    if (assessment.is_active !== 1) {
      return {
        boxClass: 'tw-bg-amber-50   tw-border   border-warning-subtle',
        titleClass: 'tw-text-amber-600',
        label: 'In schedule — inactive',
        showWindow: true,
      };
    }

    return {
      boxClass: 'tw-bg-emerald-50   tw-border   border-success-subtle',
      titleClass: 'tw-text-emerald-600',
      label: 'Available now',
      showWindow: true,
    };
  };

  const handleStartAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setShowModal(true);
  };

  const handleStartQuiz = () => {
    if (!selectedAssessment?.assessment_id || classroom_id == null || classroom_id === undefined) return;
    const encodedAssessmentId = encodeData(selectedAssessment.assessment_id);
    const encodedClassroomId = encodeData(classroom_id);
    navigate(`/user/assessmentQuiz/${encodeURIComponent(encodedAssessmentId)}?classroomid=${encodeURIComponent(encodedClassroomId)}`);
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAssessment(null);
  };

  return (
    <div className="assessment">
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6 tw-mt-2"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/3 lg:tw-w-1/3">
          <div className="seach-control">
            <SearchInput
              type="text"
              className={"!tw-pl-[35px] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjExIiBjeT0iMTEiIHI9IjgiPjwvY2lyY2xlPjxsaW5lIHgxPSIyMSIgeTE9IjIxIiB4Mj0iMTYuNjUiIHkyPSIxNi42NSI+PC9saW5lPjwvc3ZnPg==)] tw-bg-no-repeat [transition:all_0.2s_ease] tw-p-[12px_16px] tw-border-solid tw-bg-[white] tw-text-[14px] [&:focus]:tw-shadow-[0_0_0_3px_rgba(74,_108,_247,_0.1)] [&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input search-input"}
              placeholder="Search assessments..."
              aria-label="Search assessments"
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

      </div>

      {isLoading ? (
        <div className="tw-text-center tw-py-12">
          <Loader />
        </div>
      ) : (
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
          {(assessments || []).length === 0 ? (
            <div className="tw-min-w-[0px] ui-column tw-w-full">
              <NoData
                message="No Assessments Available"
                description="Assessments will appear here when they are added by the instructor."
              />
            </div>
          ) : (
            (assessments || []).map((assessment) => {
              const availability = getAvailabilityDisplay(assessment);
              return (
              <div key={assessment.assessment_id} className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2 lg:tw-w-1/3 tw-mb-6">
                <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-h-full"}>
                  <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                    {/* Header Info */}
                    <div className="tw-flex tw-justify-between tw-items-start tw-mb-2">
                      <div>
                        <div className="tw-mb-2">
                          <small className={`${assessment.is_time_based ? "!tw-text-[var(--info-color)] tw-text-sky-600" : "!tw-text-[var(--primary-color)] tw-text-brand-600"}   tw-flex tw-items-center`}>
                            <Icon
                              icon={assessment.is_time_based ? "mdi:clock-outline" : "mdi:infinity"}
                              className="tw-me-1"
                              width="14"
                              height="14"
                            />
                            {assessment.is_time_based ? 'Time-bound Assessment' : 'No time-bound Assessment'}
                          </small>
                        </div>
                        <h5 className="tw-mb-1" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minHeight: '48px', // Approximately 2 lines of text
                          marginBottom: '8px'
                        }}>
                          {assessment.assessment_name}
                        </h5>
                        <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-flex tw-items-center">
                          <Icon icon="mdi:timer-outline" className="tw-me-1" width="14" height="14" />
                          {assessment.duration} min quiz
                        </small>
                      </div>
                      <div className="tw-flex tw-flex-col tw-items-end tw-gap-2">
                        <span className={`[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge   ${assessment.is_active ? "[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--success-color)] tw-border tw-border-emerald-600 tw-text-emerald-600 tw-bg-emerald-50" : "[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--danger-color)] tw-border tw-border-rose-600 tw-text-rose-600 tw-bg-rose-50"}`}
                          style={{
                            fontWeight: '500',
                            fontSize: '12px',
                            padding: '6px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Icon
                            icon={assessment.is_active ? "mdi:check-circle" : "mdi:close-circle"}
                            width="14"
                            height="14"
                          />
                          {assessment.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {Number(assessment.is_adaptive) === 1 && (
                          <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--primary-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge tw-border tw-border-brand-600 tw-text-brand-600 tw-bg-brand-50"}>
                            Adaptive
                          </span>
                        )}
                      </div>
                    </div>

                    <hr className="tw-my-2" />
                    <div className={`tw-rounded-xl tw-p-2 tw-mb-4   ${availability.boxClass}`}>
                      <div className={`![font-weight:var(--font-weight-semibold)] !tw-text-[12px] tw-text-sm tw-font-semibold tw-mb-1   ${availability.titleClass}`}>
                        {availability.label}
                      </div>
                      {availability.hint && (
                        <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500 tw-mb-1">{availability.hint}</div>
                      )}
                      {availability.showWindow && (
                        <div
                          className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-text-slate-900"
                          style={{ fontSize: '0.9rem', lineHeight: 1.45 }}
                        >
                          {formatDate(assessment.activate_time)}
                          <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-font-normal tw-mx-1">to</span>
                          {formatDate(assessment.deactivate_time)}
                        </div>
                      )}
                    </div>




                    {/* Stats */}
                    <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-text-center tw-mb-4"}>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                        <div className="!tw-text-[var(--primary-color)] tw-font-bold tw-text-brand-600">{assessment.highest_percentage}%</div>
                        <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Highest %</small>
                      </div>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                        <div className="!tw-text-[var(--primary-color)] tw-font-bold tw-text-brand-600">{assessment.no_of_times_attended}</div>
                        <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Attempts</small>
                      </div>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                        <div className="!tw-text-[var(--primary-color)] tw-font-bold tw-text-brand-600">{assessment.total_questions}</div>
                        <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Questions</small>
                      </div>
                      <div className="tw-min-w-[0px] ui-column tw-w-1/2 md:tw-w-1/4">
                        <div className="!tw-text-[var(--primary-color)] tw-font-bold tw-text-brand-600">{assessment.pass_percentage}%</div>
                        <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Pass %</small>
                      </div>
                    </div>

                    {/* Action */}
                    <div className="tw-grid">
                      <button
                        className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-flex tw-items-center tw-justify-center tw-gap-2"}
                        onClick={() => handleStartAssessment(assessment)}
                        disabled={!isAssessmentEnabled(assessment)}
                      >
                        <Icon icon="mdi:play" width="18" />
                        <span className="![font-weight:var(--font-weight-medium)] tw-font-medium">Start Assessment</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
            })
          )}
        </div>
      )}

      {/* Assessment Info Modal */}
      {showModal && selectedAssessment && (
        <Dialog show onHide={handleCloseModal} size="lg">
              <Dialog.Header closeButton>
                <Dialog.Title >
                  <Icon icon="mdi:clipboard-text" className="tw-me-2" width="24" height="24" />
                  Assessment Information
                </Dialog.Title>

              </Dialog.Header>
              <Dialog.Body >
                <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
                  <div className="tw-min-w-[0px] ui-column tw-w-full">
                    <h6 className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-mb-4">{selectedAssessment.assessment_name}</h6>

                    <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6"}>
                      <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <Icon icon="mdi:clock-outline" className="!tw-text-[var(--info-color)] tw-text-sky-600 tw-me-2" width="18" />
                          <strong>Duration:</strong>
                          <span className="tw-ms-2">
                            {selectedAssessment.duration > 0 ? `${selectedAssessment.duration} minutes` : 'Untimed'}
                          </span>
                        </div>
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <Icon icon="mdi:help-circle" className="!tw-text-[var(--info-color)] tw-text-sky-600 tw-me-2" width="18" />
                          <strong>Total Questions:</strong>
                          <span className="tw-ms-2">{selectedAssessment.total_questions}</span>
                        </div>
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <Icon icon="mdi:target" className="!tw-text-[var(--info-color)] tw-text-sky-600 tw-me-2" width="18" />
                          <strong>Pass Percentage:</strong>
                          <span className="tw-ms-2">{selectedAssessment.pass_percentage}%</span>
                        </div>
                        {Number(selectedAssessment.is_adaptive) === 1 && (
                          <div className="tw-flex tw-items-center tw-mb-2">
                            <Icon icon="mdi:stairs-up" className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-me-2" width="18" />
                            <strong>Mode:</strong>
                            <span className="tw-ms-2">Adaptive Assessment</span>
                          </div>
                        )}
                      </div>
                      <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <Icon icon="mdi:chart-line" className="!tw-text-[var(--success-color)] tw-text-emerald-600 tw-me-2" width="18" />
                          <strong>Your Best Score:</strong>
                          <span className="tw-ms-2">{selectedAssessment.highest_percentage}%</span>
                        </div>
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <Icon icon="mdi:repeat" className="!tw-text-[var(--warning-color)] tw-text-amber-600 tw-me-2" width="18" />
                          <strong>Attempts Made:</strong>
                          <span className="tw-ms-2">{selectedAssessment.no_of_times_attended}</span>
                        </div>
                        <div className="tw-flex tw-items-center tw-mb-2">
                          <Icon
                            icon={selectedAssessment.is_active ? "mdi:check-circle" : "mdi:close-circle"}
                            className={`${selectedAssessment.is_active ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : "!tw-text-[var(--danger-color)] tw-text-rose-600"}   tw-me-2`}
                            width="18"
                          />
                          <strong>Status:</strong>
                          <span className={`tw-ms-2   ${selectedAssessment.is_active ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : "!tw-text-[var(--danger-color)] tw-text-rose-600"}`}>
                            {selectedAssessment.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {selectedAssessment.is_time_based === 1 && (
                      <div className="tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(224_242_254_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(240_249_255_/_var(--tw-bg-opacity,_1))] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] tw-text-[rgb(12_74_110_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-info tw-mb-4">
                        <div className="tw-flex tw-items-start">
                          <Icon icon="mdi:information" className="tw-me-2 tw-mt-1" width="18" />
                          <div>
                            <strong>Time-bound Assessment</strong>
                            <div className="tw-mt-1">
                              <small>
                                Available from: {formatDate(selectedAssessment.activate_time)}
                                <br />
                                Available until: {formatDate(selectedAssessment.deactivate_time)}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedAssessment.is_time_based === 0 && (
                      <div className="tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-border-opacity:1] tw-border-[rgb(209_250_229_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(236_253_245_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(6_78_59_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-success tw-mb-4">
                        <div className="tw-flex tw-items-center">
                          <Icon icon="mdi:infinity" className="tw-me-2" width="18" />
                          <strong>No Time Restrictions - Available anytime</strong>
                        </div>
                      </div>
                    )}

                    <div className="tw-mb-4 tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-border-opacity:1] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-warning">
                      <div className="tw-flex tw-items-start">
                        <Icon icon="mdi:alert" className="tw-me-2 tw-mt-1" width="18" />
                                                 <div>
                           <strong>Important Instructions:</strong>
                           <ul className="tw-mt-2 tw-mb-0">
                             <li>Make sure you have a stable internet connection</li>
                             <li>Once started, the assessment must be completed in one sitting</li>
                             {selectedAssessment.duration > 0 && (
                               <li>You have {selectedAssessment.duration} minutes to complete the assessment</li>
                             )}
                             {Number(selectedAssessment.is_adaptive) === 1 ? (
                               <li>Question difficulty adjusts by your performance</li>
                             ) : (
                               <li>You can navigate between questions during the assessment</li>
                             )}
                             {selectedAssessment.duration > 0 ? (
                               <li>In time-bound assessments, answers will be auto-submitted when time expires</li>
                             ) : (
                               <li>Your answers are saved automatically when you select them</li>
                             )}
                           </ul>
                         </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Body>
              <Dialog.Footer >
                <button
                  type="button"
                  className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary"}
                  onClick={handleCloseModal}
                >
                  <Icon icon="mdi:close" className="tw-me-1" width="16" />
                  Cancel
                </button>
                <button
                  type="button"
                  className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                  onClick={handleStartQuiz}
                  disabled={!isAssessmentEnabled(selectedAssessment)}
                >
                  <Icon icon="mdi:play" className="tw-me-1" width="16" />
                  Start Quiz
                </button>
              </Dialog.Footer>
            </Dialog>
      )}
    </div>
  );
}

export default Assessments;
